<script setup lang="ts">
const {
  modelValue = '',
  suggestionType = '',
  placeholder = 'Search...',
  ariaLabel = 'Search',
  name = 'search',
  size = 200,
  clearable = true,
} = defineProps<{
  modelValue?: string,
  suggestionType?: string,
  placeholder?: string,
  ariaLabel?: string,
  name?: string,
  size?: number,
  clearable?: boolean,
  formClass?: string | string[] | Record<string, boolean>,
}>();

type SuggestionGroup = {
  title: string
  icon: string
  items: string[]
}

type SuggestionItem = {
  text: string
  icon: string
  group_title: string
}

const SUGGESTION_GROUPS: Record<string, SuggestionGroup[]> = {
  videos: [
    {title: 'Technologies & Frameworks', icon: 'code-bracket', items: ['React hooks tutorial', 'Next.js app router', 'TypeScript best practices', 'Vue 3 composition API', 'Angular signals', 'Docker containerization', 'Kubernetes deployment']},
    {title: 'AI & Automation', icon: 'sparkles', items: ['AI coding assistant', 'Machine learning fundamentals', 'GPT API integration', 'Computer vision basics', 'Neural networks explained', 'LangChain tutorial', 'Prompt engineering']},
    {title: 'Career & Skills', icon: 'academic-cap', items: ['System design interview', 'Code review best practices', 'Technical leadership', 'Remote work productivity', 'Open source contribution', 'Portfolio projects', 'Freelancing tips', 'a11y']},
    {title: 'Infrastructure & DevOps', icon: 'command-line', items: ['Microservices architecture', 'Database optimization', 'API design patterns', 'CI/CD pipelines', 'Cloud deployment', 'Monitoring and logging', 'Security best practices']}
  ],
  jobs: [
    {title: 'Technologies & Frameworks', icon: 'code-bracket', items: ['React']},
    {title: 'Technologies & Frameworks', icon: 'code-bracket', items: ['PHP']},
    {title: 'Technologies & Frameworks', icon: 'code-bracket', items: ['iOS Swift']},
    {title: 'Infrastructure & DevOps', icon: 'squares-2x2', items: ['Kubernetes']},
  ],
  articles: [
    {title: 'Technologies & Frameworks', icon: 'code-bracket', items: ['React', 'Python', 'TypeScript', 'Javascript']},
    {title: 'AI & Automation', icon: 'sparkles', items: ['AI coding assistant', 'Prompt engineering', 'ChatGPT']},
    {title: 'Soft skills', icon: 'academic-cap', items: ['Public Speaking', 'Security Concepts', 'Accessibility']},
    {title: 'Category', icon: 'squares-2x2', items: ['Software Development', 'Tips, Tricks, Tools' , 'Security & Privacy', 'Career & Culture']},
  ]
} as const

const randomSuggestions = () => {
  const groups = SUGGESTION_GROUPS[suggestionType] ?? [];

  return groups.map((group, i) => {
    const idx = Math.floor(Math.random() * group.items.length);
    const item = group.items[idx]!;

    return {
      text: item,
      icon: group.icon,
      group_title: group.title
    }
  });
}

const suggestions = useState<SuggestionItem[]>('search-suggestions', () => randomSuggestions());

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'submit', value: string): void
  (e: 'clear'): void
}>()

const inputRef = ref<HTMLInputElement | null>(null);
const isActive = ref(false);
const slots = useSlots();

const useSuggestion = async (s: SuggestionItem) => {
  emit('update:modelValue', s.text);
  await nextTick();
  onSubmit();
}

const onInput = (e: Event) => {
  const target = e.target as HTMLInputElement;
  emit('update:modelValue', target.value);
}

const lastSubmittedQ = ref<string>('');
const onSubmit = (e?: Event) => {
  e?.preventDefault();
  const q = (modelValue ?? '').trim();
  if(q !== lastSubmittedQ.value) {
    emit('submit', q);
    lastSubmittedQ.value = q;
  }
}

const clear = async () => {
  emit('update:modelValue', '');
  emit('clear');
  lastSubmittedQ.value = '';
  await nextTick();
  inputRef.value?.focus();
}

watch(() => modelValue, async (newValue, oldValue) => {
  if(!newValue && newValue !== oldValue && newValue !== lastSubmittedQ.value) {
    await clear();
  }
})

defineExpose({ focus: () => inputRef.value?.focus() })
</script>
<template>
  <div class="wad4-search">
    <form class="wad4-search-form" :class="{formClass, 'wad4-search-form--active' : isActive }" @submit="onSubmit">
      <input
          :value="modelValue"
          @input="onInput"
          ref="inputRef"
          :maxlength="200"
          :placeholder="placeholder"
          :aria-label="ariaLabel"
          :size="size"
          type="text"
          :name="name"
          autocomplete="off"
          data-search-form-target="input"
          @focus="isActive = true"
          @blur="isActive = false"
      />
      <!-- Clear button -->
      <svg
          v-if="clearable && (modelValue?.length ?? 0) > 0"
          @click="clear"
          class="wad4-search-form__clear"
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          role="button"
          tabindex="0"
          aria-label="Clear search"
      >
        <path d="M6 18L18 6M6 6L18 18" stroke="#706E6B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <slot name="input-right-slot"/>
      <!-- Screen-reader only submit input -->
      <input
          type="submit"
          name="commit"
          value="Search"
          data-search-form-target="submit"
          data-disable-with="Search"
          class="sr-only"
          aria-label="Submit search"
          aria-disabled="false"
      />
    </form>
    <div class="wad4-search__lower-slot" v-if="slots['lower-slot']">
      <slot name="lower-slot"/>
    </div>
    <div v-if="suggestionType" class="wad4-search__suggestions">
      Try:
      <button v-for="s in suggestions" @pointerup="useSuggestion(s)" class="wad4-button-ghost">
          <span class="wad4-search__suggestions__icon" aria-hidden="true">
            <svg v-if="s.icon === 'code-bracket'" xmlns="http://www.w3.org/2000/svg" width="12" height="10" viewBox="0 0 12 10" fill="none">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M3.3176 1.93189C3.40187 2.01626 3.44921 2.13064 3.44921 2.24989C3.44921 2.36914 3.40187 2.48351 3.3176 2.56789L1.0856 4.79989L3.3176 7.03189C3.39709 7.11719 3.44037 7.23002 3.43831 7.3466C3.43625 7.46318 3.38902 7.57441 3.30658 7.65686C3.22413 7.73931 3.1129 7.78654 2.99632 7.78859C2.87974 7.79065 2.76691 7.74738 2.6816 7.66789L0.131604 5.11789C0.0473338 5.03351 0 4.91914 0 4.79989C0 4.68064 0.0473338 4.56626 0.131604 4.48189L2.6816 1.93189C2.76598 1.84762 2.88035 1.80028 2.9996 1.80028C3.11885 1.80028 3.23323 1.84762 3.3176 1.93189ZM7.7816 1.93189C7.86598 1.84762 7.98035 1.80028 8.0996 1.80028C8.21885 1.80028 8.33323 1.84762 8.4176 1.93189L10.9676 4.48189C11.0519 4.56626 11.0992 4.68064 11.0992 4.79989C11.0992 4.91914 11.0519 5.03351 10.9676 5.11789L8.4176 7.66789C8.3323 7.74738 8.21947 7.79065 8.10289 7.78859C7.98631 7.78654 7.87508 7.73931 7.79263 7.65686C7.71018 7.57441 7.66296 7.46318 7.6609 7.3466C7.65884 7.23002 7.70212 7.11719 7.7816 7.03189L10.0136 4.79989L7.7816 2.56789C7.69733 2.48351 7.65 2.36914 7.65 2.24989C7.65 2.13064 7.69733 2.01626 7.7816 1.93189ZM6.3758 0.0064864C6.43409 0.0165021 6.48982 0.0379072 6.53983 0.0694778C6.58983 0.101048 6.63312 0.142165 6.66722 0.190478C6.70133 0.23879 6.72557 0.293351 6.73858 0.35104C6.75158 0.408729 6.75308 0.468415 6.743 0.526686L5.243 9.22669C5.22119 9.34285 5.15455 9.44577 5.05748 9.51321C4.96041 9.58064 4.8407 9.60718 4.72423 9.58708C4.60775 9.56699 4.50386 9.50187 4.435 9.40581C4.36613 9.30975 4.33783 9.19045 4.3562 9.07369L5.8562 0.373686C5.87644 0.256102 5.94254 0.151366 6.03998 0.0825076C6.13741 0.013649 6.25821 -0.0136956 6.3758 0.0064864Z" fill="currentColor" fill-opacity="0.6"/>
            </svg>
            <svg v-else-if="s.icon === 'squares-2x2'" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M1.5 3C1.5 2.60218 1.65804 2.22064 1.93934 1.93934C2.22064 1.65804 2.60218 1.5 3 1.5H4.125C4.52282 1.5 4.90436 1.65804 5.18566 1.93934C5.46696 2.22064 5.625 2.60218 5.625 3V4.125C5.625 4.52282 5.46696 4.90436 5.18566 5.18566C4.90436 5.46696 4.52282 5.625 4.125 5.625H3C2.60218 5.625 2.22064 5.46696 1.93934 5.18566C1.65804 4.90436 1.5 4.52282 1.5 4.125V3ZM6.375 3C6.375 2.60218 6.53304 2.22064 6.81434 1.93934C7.09564 1.65804 7.47718 1.5 7.875 1.5H9C9.39782 1.5 9.77936 1.65804 10.0607 1.93934C10.342 2.22064 10.5 2.60218 10.5 3V4.125C10.5 4.52282 10.342 4.90436 10.0607 5.18566C9.77936 5.46696 9.39782 5.625 9 5.625H7.875C7.47718 5.625 7.09564 5.46696 6.81434 5.18566C6.53304 4.90436 6.375 4.52282 6.375 4.125V3ZM1.5 7.875C1.5 7.47718 1.65804 7.09564 1.93934 6.81434C2.22064 6.53304 2.60218 6.375 3 6.375H4.125C4.52282 6.375 4.90436 6.53304 5.18566 6.81434C5.46696 7.09564 5.625 7.47718 5.625 7.875V9C5.625 9.39782 5.46696 9.77936 5.18566 10.0607C4.90436 10.342 4.52282 10.5 4.125 10.5H3C2.60218 10.5 2.22064 10.342 1.93934 10.0607C1.65804 9.77936 1.5 9.39782 1.5 9V7.875ZM6.375 7.875C6.375 7.47718 6.53304 7.09564 6.81434 6.81434C7.09564 6.53304 7.47718 6.375 7.875 6.375H9C9.39782 6.375 9.77936 6.53304 10.0607 6.81434C10.342 7.09564 10.5 7.47718 10.5 7.875V9C10.5 9.39782 10.342 9.77936 10.0607 10.0607C9.77936 10.342 9.39782 10.5 9 10.5H7.875C7.47718 10.5 7.09564 10.342 6.81434 10.0607C6.53304 9.77936 6.375 9.39782 6.375 9V7.875Z" fill="currentColor"/>
            </svg>
            <svg v-else-if="s.icon === 'sparkles'" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M9.58804 1.08216C9.5605 0.946529 9.48692 0.824587 9.37976 0.736999C9.27259 0.64941 9.13845 0.601562 9.00004 0.601562C8.86164 0.601563 8.72749 0.64941 8.62033 0.736999C8.51317 0.824587 8.43959 0.946529 8.41204 1.08216L8.26804 1.79736C8.24489 1.91354 8.18786 2.02026 8.10415 2.10408C8.02044 2.1879 7.91379 2.24506 7.79764 2.26836L7.08244 2.41116C6.94616 2.43812 6.82345 2.51153 6.73525 2.61887C6.64706 2.72622 6.59885 2.86084 6.59885 2.99976C6.59885 3.13869 6.64706 3.27331 6.73525 3.38066C6.82345 3.488 6.94616 3.56141 7.08244 3.58836L7.79764 3.73116C7.9139 3.75436 8.02068 3.81148 8.10451 3.8953C8.18833 3.97913 8.24545 4.08591 8.26864 4.20216L8.41144 4.91736C8.4384 5.05365 8.51181 5.17636 8.61915 5.26456C8.7265 5.35275 8.86112 5.40096 9.00004 5.40096C9.13897 5.40096 9.27359 5.35275 9.38094 5.26456C9.48828 5.17636 9.56169 5.05365 9.58864 4.91736L9.73144 4.20216C9.75464 4.08591 9.81176 3.97913 9.89558 3.8953C9.97941 3.81148 10.0862 3.75436 10.2024 3.73116L10.9176 3.58836C11.0539 3.56141 11.1766 3.488 11.2648 3.38066C11.353 3.27331 11.4012 3.13869 11.4012 2.99976C11.4012 2.86084 11.353 2.72622 11.2648 2.61887C11.1766 2.51153 11.0539 2.43812 10.9176 2.41116L10.2024 2.26836C10.0862 2.24517 9.97941 2.18805 9.89558 2.10423C9.81176 2.0204 9.75464 1.91362 9.73144 1.79736L9.58864 1.08216H9.58804ZM4.16944 3.41016C4.12971 3.29056 4.05332 3.1865 3.95111 3.11276C3.84891 3.03902 3.72608 2.99933 3.60004 2.99933C3.47401 2.99933 3.35118 3.03902 3.24897 3.11276C3.14677 3.1865 3.07038 3.29056 3.03064 3.41016L2.62084 4.64076C2.5914 4.72923 2.54174 4.80961 2.47582 4.87554C2.40989 4.94146 2.32951 4.99112 2.24104 5.02056L1.01044 5.43036C0.890839 5.4701 0.786784 5.54649 0.71304 5.64869C0.639296 5.7509 0.599609 5.87373 0.599609 5.99976C0.599609 6.1258 0.639296 6.24863 0.71304 6.35083C0.786784 6.45304 0.890839 6.52943 1.01044 6.56916L2.24104 6.97956C2.32944 7.00895 2.40978 7.05851 2.47571 7.12433C2.54163 7.19015 2.59132 7.27041 2.62084 7.35876L3.03064 8.58936C3.07038 8.70897 3.14677 8.81302 3.24897 8.88677C3.35118 8.96051 3.47401 9.0002 3.60004 9.0002C3.72608 9.0002 3.84891 8.96051 3.95111 8.88677C4.05332 8.81302 4.12971 8.70897 4.16944 8.58936L4.57924 7.35876C4.60869 7.2703 4.65834 7.18992 4.72427 7.12399C4.7902 7.05806 4.87058 7.00841 4.95904 6.97896L6.18964 6.56916C6.30925 6.52943 6.4133 6.45304 6.48705 6.35083C6.56079 6.24863 6.60048 6.1258 6.60048 5.99976C6.60048 5.87373 6.56079 5.7509 6.48705 5.64869C6.4133 5.54649 6.30925 5.4701 6.18964 5.43036L4.95904 5.02056C4.87058 4.99112 4.7902 4.94146 4.72427 4.87554C4.65834 4.80961 4.60869 4.72923 4.57924 4.64076L4.16944 3.41016ZM8.36944 8.21016C8.32971 8.09056 8.25332 7.9865 8.15111 7.91276C8.04891 7.83902 7.92608 7.79933 7.80004 7.79933C7.67401 7.79933 7.55118 7.83902 7.44897 7.91276C7.34677 7.9865 7.27038 8.09056 7.23064 8.21016L7.12024 8.54076C7.09086 8.62916 7.04129 8.7095 6.97548 8.77543C6.90966 8.84135 6.8294 8.89104 6.74104 8.92056L6.41044 9.03036C6.29084 9.0701 6.18678 9.14649 6.11304 9.24869C6.0393 9.3509 5.99961 9.47373 5.99961 9.59976C5.99961 9.7258 6.0393 9.84863 6.11304 9.95083C6.18678 10.053 6.29084 10.1294 6.41044 10.1692L6.74104 10.279C6.82951 10.3084 6.90989 10.3581 6.97582 10.424C7.04174 10.4899 7.0914 10.5703 7.12084 10.6588L7.23064 10.9894C7.27038 11.109 7.34677 11.213 7.44897 11.2868C7.55118 11.3605 7.67401 11.4002 7.80004 11.4002C7.92608 11.4002 8.04891 11.3605 8.15111 11.2868C8.25332 11.213 8.32971 11.109 8.36944 10.9894L8.47984 10.6588C8.50923 10.5704 8.55879 10.49 8.62461 10.4241C8.69043 10.3582 8.77069 10.3085 8.85904 10.279L9.18964 10.1692C9.30925 10.1294 9.4133 10.053 9.48705 9.95083C9.56079 9.84863 9.60048 9.7258 9.60048 9.59976C9.60048 9.47373 9.56079 9.3509 9.48705 9.24869C9.4133 9.14649 9.30925 9.0701 9.18964 9.03036L8.85904 8.91996C8.77065 8.89058 8.6903 8.84101 8.62438 8.7752C8.55846 8.70938 8.50877 8.62912 8.47924 8.54076L8.36944 8.21016Z" fill="currentColor" fill-opacity="0.6"/>
            </svg>
            <svg v-else-if="s.icon === 'academic-cap'" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M5.79819 0.791826C5.86076 0.760468 5.92979 0.744141 5.99979 0.744141C6.06978 0.744141 6.13881 0.760468 6.20139 0.791826C7.96539 1.67574 9.61683 2.76836 11.1202 4.04623C11.1812 4.09808 11.2273 4.1653 11.2537 4.24094C11.2801 4.31657 11.2859 4.39788 11.2703 4.47647C11.2548 4.55505 11.2186 4.62807 11.1654 4.68797C11.1122 4.74788 11.044 4.7925 10.9678 4.81723C9.29623 5.35934 7.70881 6.13285 6.25179 7.11523C6.17737 7.16552 6.08961 7.1924 5.99979 7.1924C5.90997 7.1924 5.8222 7.16552 5.74779 7.11523C5.34194 6.84177 4.92562 6.58418 4.49979 6.34303V5.63683C4.49979 5.49043 4.56939 5.35903 4.68099 5.28163C5.31926 4.83918 5.98125 4.43197 6.66399 4.06183C6.76718 4.00408 6.8435 3.90803 6.87643 3.79445C6.90936 3.68088 6.89627 3.55891 6.83997 3.45491C6.78368 3.35092 6.68871 3.27326 6.57561 3.23874C6.46251 3.20421 6.34036 3.2156 6.23559 3.27043C5.5237 3.6564 4.83347 4.08103 4.16799 4.54243C3.99202 4.66534 3.84843 4.82903 3.74948 5.01951C3.65054 5.20998 3.59917 5.42158 3.59979 5.63623V5.86423C2.77078 5.4522 1.91244 5.10204 1.03179 4.81663C0.955592 4.7919 0.88738 4.74728 0.834194 4.68737C0.781009 4.62747 0.744779 4.55445 0.729245 4.47587C0.713711 4.39728 0.719437 4.31597 0.745831 4.24034C0.772225 4.1647 0.818331 4.09748 0.879386 4.04563C2.38275 2.76776 4.03418 1.67514 5.79819 0.791225V0.791826ZM3.59979 6.87583C3.11958 6.62201 2.62807 6.39017 2.12679 6.18103C2.02499 6.80061 1.94693 7.42387 1.89279 8.04943C1.88482 8.14018 1.90459 8.23122 1.94948 8.3105C1.99437 8.38978 2.06226 8.45357 2.14419 8.49343C2.46099 8.64703 2.77179 8.81143 3.07659 8.98543C2.95059 9.17983 2.80359 9.36343 2.63319 9.53383C2.58897 9.57502 2.55351 9.6247 2.52892 9.6799C2.50432 9.7351 2.4911 9.79469 2.49003 9.85511C2.48897 9.91553 2.50008 9.97555 2.52271 10.0316C2.54535 10.0876 2.57903 10.1385 2.62176 10.1812C2.66449 10.224 2.71539 10.2577 2.77143 10.2803C2.82746 10.3029 2.88748 10.314 2.9479 10.313C3.00832 10.3119 3.06791 10.2987 3.12311 10.2741C3.17831 10.2495 3.22799 10.214 3.26919 10.1698C3.49119 9.94843 3.68319 9.70783 3.84519 9.45403C4.49768 9.87775 5.11834 10.3486 5.70219 10.8628C5.78438 10.9353 5.8902 10.9753 5.99979 10.9753C6.10937 10.9753 6.21519 10.9353 6.29739 10.8628C7.3715 9.91653 8.56828 9.11935 9.85539 8.49283C9.93732 8.45309 10.0053 8.38944 10.0503 8.31027C10.0953 8.23111 10.1152 8.14015 10.1074 8.04943C10.0532 7.42387 9.97517 6.80062 9.87339 6.18103C8.78072 6.63701 7.73583 7.19995 6.75399 7.86163C6.53111 8.01175 6.26851 8.09195 5.99979 8.09195C5.73107 8.09195 5.46846 8.01175 5.24559 7.86163C4.99959 7.69663 4.74999 7.53703 4.49559 7.38343C4.46426 8.11856 4.23994 8.83308 3.84519 9.45403C3.59345 9.29081 3.33715 9.13414 3.07659 8.98543C3.41894 8.45309 3.60059 7.83335 3.59979 7.20043V6.87643V6.87583Z" fill="currentColor" fill-opacity="0.6"/>
            </svg>
            <svg v-else-if="s.icon === 'command-line'" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M1.94961 1.80078C1.59157 1.80078 1.24819 1.94301 0.995015 2.19619C0.741841 2.44936 0.599609 2.79274 0.599609 3.15078V8.85078C0.599609 9.20882 0.741841 9.5522 0.995015 9.80538C1.24819 10.0585 1.59157 10.2008 1.94961 10.2008H10.0496C10.2269 10.2008 10.4024 10.1659 10.5662 10.098C10.73 10.0302 10.8788 9.93073 11.0042 9.80538C11.1296 9.68002 11.229 9.53119 11.2968 9.3674C11.3647 9.20361 11.3996 9.02807 11.3996 8.85078V3.15078C11.3996 2.9735 11.3647 2.79795 11.2968 2.63416C11.229 2.47037 11.1296 2.32155 11.0042 2.19619C10.8788 2.07083 10.73 1.97139 10.5662 1.90354C10.4024 1.8357 10.2269 1.80078 10.0496 1.80078H1.94961ZM2.51541 7.05198C2.47578 7.00807 2.44519 6.95678 2.42539 6.90104C2.4056 6.8453 2.39699 6.78621 2.40005 6.72714C2.40312 6.66807 2.4178 6.61018 2.44326 6.55679C2.46871 6.5034 2.50444 6.45555 2.54841 6.41598L3.67641 5.40078L2.54841 4.38498C2.50309 4.34583 2.46605 4.29802 2.43944 4.24437C2.41284 4.19071 2.39721 4.13229 2.39348 4.07252C2.38975 4.01274 2.39798 3.95283 2.4177 3.89628C2.43743 3.83973 2.46824 3.78769 2.50833 3.7432C2.54843 3.69872 2.597 3.66268 2.6512 3.63721C2.7054 3.61173 2.76414 3.59733 2.82398 3.59485C2.88381 3.59237 2.94354 3.60186 2.99966 3.62277C3.05579 3.64367 3.10717 3.67556 3.15081 3.71658L4.65081 5.06658C4.6976 5.10878 4.73502 5.16033 4.76062 5.2179C4.78623 5.27547 4.79946 5.33777 4.79946 5.40078C4.79946 5.46379 4.78623 5.5261 4.76062 5.58367C4.73502 5.64124 4.6976 5.69279 4.65081 5.73498L3.15081 7.08498C3.1069 7.12461 3.05561 7.1552 2.99987 7.175C2.94413 7.19479 2.88504 7.2034 2.82597 7.20034C2.7669 7.19727 2.70901 7.18259 2.65562 7.15713C2.60223 7.13168 2.55498 7.09595 2.51541 7.05198ZM5.84961 6.15078C5.73026 6.15078 5.6158 6.19819 5.53141 6.28258C5.44702 6.36697 5.39961 6.48143 5.39961 6.60078C5.39961 6.72013 5.44702 6.83459 5.53141 6.91898C5.6158 7.00337 5.73026 7.05078 5.84961 7.05078H7.34961C7.46896 7.05078 7.58342 7.00337 7.66781 6.91898C7.7522 6.83459 7.79961 6.72013 7.79961 6.60078C7.79961 6.48143 7.7522 6.36697 7.66781 6.28258C7.58342 6.19819 7.46896 6.15078 7.34961 6.15078H5.84961Z" fill="currentColor" fill-opacity="0.6"/>
            </svg>
          </span>
        {{ s.text }}
      </button>
    </div>
  </div>
</template>

<style lang="scss">
.wad4-search {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  flex-direction: column;
  gap: $wad4-spacing-md;
  align-self: stretch;

  &-form {
    display: flex;
    max-width: 768px;
    width: 100%;
    justify-content: center;
    align-items: center;
    gap: 24px;
    overflow: hidden;
    position: relative;
    border-radius: $wad4-border-radius-full;
    border: 2px solid $wad4-c-border-border-brand;
    background: $wad4-c-background-bg-primary;
    box-shadow: 0 10px 15px -3px $wad4-bc-shadow-neutral-10, 0 4px 6px -2px $wad4-bc-shadow-neutral-5;
    padding: 0 $wad4-spacing-3xl;

    &:hover:not(.wad4-search-form--active):not(:focus-within) {
      box-shadow: 0 20px 25px -5px $wad4-bc-shadow-neutral-10, 0 10px 10px -5px $wad4-bc-shadow-neutral-4;
    }

    &--active {
      border: 2px solid $wad4-c-background-bg-brand-solid;
      box-shadow: 0 0 0 4px rgba(194, 216, 255, 0.80), 0 20px 25px -5px $wad4-bc-shadow-neutral-10, 0 10px 10px -5px $wad4-bc-shadow-neutral-4;
    }

    input[type="text"] {
      flex: 1 0 0;
      color: $wad4-c-text-text-primary;
      font-size: 18px;
      font-style: normal;
      font-weight: 400;
      line-height: 44px;
      padding: 0;
      border: 0;
      box-shadow: none;
      outline: none;
      min-width: 0;
      background: $wad4-c-background-bg-primary;

      &::placeholder {
        color: $wad4-c-text-text-tertiery;
        opacity: 1;
      }

      @include mqBreakPoint(0, $wad4-breakpoint-mobile-max) {
        font-size: 14px;
      }
    }

    &__clear {
      cursor: pointer;
    }

    & .sr-only {
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border-width: 0;
      width: 1px;
      height: 1px;
      margin: -1px;
      padding: 0;
      position: absolute;
      overflow: hidden;
      right: 0;
    }
  }

  &__suggestions {
    display: flex;
    max-width: 768px;
    justify-content: center;
    align-items: center;
    gap: $wad4-spacing-md;
    color: $wad4-c-text-text-tertiery;
    @include text-size-mixin('xs');
    flex-wrap: wrap;

    &__icon {
      height: 12px;
    }
  }

  .wad4-base-checkbox {
    &__label {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    &__content {
      color: $wad4-c-text-text-tertiery;
    }
  }
}
</style>
